import { SimpleLocation } from "./location";
import { Promotable } from "./promotable";
import { LiteralObject } from "../../types/literal_object";

export enum PromotionStatus {
  Active = "active",
  Expired = "expired",
  All = "all",
}

export interface Promotion {
  id: string;
  name: string | null;
  code: string;
  description: string;
  startDate: string;
  endDate: string | null;
  numberOfOrders: number;
  revenue: number;
  currency: string;
  status: PromotionStatus;
  lastUpdated: string;
  readOnly: boolean;
}

export const toPromotion = (data: any): Promotion => {
  return {
    id: data.id,
    name: data.name,
    code: data.code,
    description: data.description,
    startDate: data.start_date,
    endDate: data.end_date,
    numberOfOrders: data.number_of_orders,
    revenue: data.revenue,
    currency: data.currency,
    status: data.status,
    lastUpdated: data.last_updated,
    readOnly: data.read_only,
  };
};

export enum LocationType {
  All = "all",
  Specific = "specific",
}

export enum DiscountType {
  Percentage = "percentage",
  Amount = "amount",
}

export interface PromotionInput {
  name: string;
  code: string;
  description: string;
  discountType: DiscountType | null;
  discountValue: string | undefined;
  minHoursBookedChecked: boolean;
  minHoursBooked: number | undefined | null;
  minDaysBookedChecked: boolean;
  minDaysBooked: number | undefined | null;
  limitUsageChecked: boolean;
  limitUsage: number | undefined | null;
  startEndDateChecked: boolean;
  startDate: string;
  endDate: string;
  addUsageDateChecked: boolean;
  applicableStartDate: string;
  applicableEndDate: string;
  locationType: LocationType;
  locationOfferings: LocationOfferingsInput[];
  allLocationsOfferings: AllLocationsOfferingsInput;
  disabledPromotionCodeEditing?: boolean | undefined;
  orderNonRefundable: boolean;
}

export interface LocationOfferingsInput {
  location: SimpleLocation;
  promotables: Array<Promotable>;
}

export interface AllLocationsOfferingsInput {
  allPromotables: boolean;
  promotables: Array<Promotable>;
}

export const prepareCreateCampaignParams = (input: PromotionInput) => {
  const promotionItems = [];
  if (input.locationType === LocationType.All) {
    const { allPromotables, promotables } = input.allLocationsOfferings;
    if (allPromotables) {
      promotionItems.push({
        location_id: null,
        all_offerings: true,
      });
    } else {
      promotionItems.push({
        location_id: null,
        promotable: promotables.map((promotable) => ({
          id: promotable.id,
          type: promotable.type,
        })),
      });
    }
  } else {
    promotionItems.push(
      ...input.locationOfferings.map((lo) => ({
        location_id: lo.location.id,
        promotable: lo.promotables.map((promotable) => ({
          id: promotable.id,
          type: promotable.type,
        })),
      }))
    );
  }

  return {
    name: input.name,
    promotion_code: input.code,
    description: input.description,
    start_date: input.startDate,
    end_date: input.endDate,
    order_non_refundable: input.orderNonRefundable,
    limit_usage_per_email: input.limitUsage,
    offer: {
      type: input.discountType,
      value: input.discountValue,
    },
    rule: {
      min_days_booked: input.minDaysBooked,
      min_hours_booked: input.minHoursBooked,
      applicable_start_date: input.applicableStartDate,
      applicable_end_date: input.applicableEndDate,
    },
    promotion_items: promotionItems,
  };
};

export interface PromotionItemOffer {
  type: DiscountType;
  value: number;
}

export interface PromotionItemRule {
  minHoursBooked: number | null;
  minDaysBooked: number | null;
  limitUsage: number | null;
  applicableStartDate: string | null;
  applicableEndDate: string | null;
}

export interface PromotionItem {
  id: string;
  promotableId: string;
  promotableType: string;
  locationId: string | null;
}

export interface PromotionDetail {
  id: string;
  name: string | null;
  code: string;
  description: string;
  startDate: string;
  endDate: string | null;
  numberOfOrders: number;
  orderNonRefundable: boolean;
  limitUsagePerEmail: number | null;
  offer: PromotionItemOffer;
  rule: PromotionItemRule;
  promotionItems: Array<PromotionItem>;
}

const convertToNumber = (value: string) => {
  const integerValue = parseInt(value);
  const floatValue = parseFloat(value);

  return integerValue === floatValue ? integerValue : floatValue;
};

export const toPromotionDetail = (data: any): PromotionDetail => {
  return {
    id: data.id,
    name: data.name,
    code: data.code,
    description: data.description,
    startDate: data.start_date,
    endDate: data.end_date,
    orderNonRefundable: data.order_non_refundable,
    numberOfOrders: data.number_of_orders,
    limitUsagePerEmail: data.limit_usage_per_email,
    offer: {
      type: data.offer.type,
      value: data.offer.value ? convertToNumber(data.offer.value) : 0,
    },
    rule: {
      minHoursBooked: data.rule.min_hours_booked
        ? convertToNumber(data.rule.min_hours_booked)
        : null,
      minDaysBooked: data.rule.min_days_booked
        ? convertToNumber(data.rule.min_days_booked)
        : null,
      limitUsage: data.rule.limit_usage,
      applicableStartDate: data.rule.applicable_start_date,
      applicableEndDate: data.rule.applicable_end_date,
    },
    promotionItems: data.promotion_items.map((item: any) => ({
      id: item.id,
      promotableId: item.promotable_id,
      promotableType: item.promotable_type,
      locationId: item.location_id,
    })),
  };
};

export const prepareUpdateParams = (
  promotionDetail: PromotionDetail,
  promotionInput: PromotionInput
) => {
  const currentPromotionItems = promotionDetail.promotionItems;
  const promotionItems: LiteralObject[] = [];
  if (promotionInput.locationType === LocationType.All) {
    const { promotables } = promotionInput.allLocationsOfferings;
    promotables.forEach((promotable) => {
      const promotionItem: LiteralObject = {
        promotable_id: promotable.id,
        promotable_type: promotable.type,
        location_id: null,
      };
      const currentPromotionItem = currentPromotionItems.find(
        (pi) =>
          pi.locationId === null &&
          pi.promotableType === promotable.type &&
          pi.promotableId === promotable.id
      );
      if (currentPromotionItem) {
        promotionItem["id"] = currentPromotionItem.id;
      }
      promotionItems.push(promotionItem);
    });
  } else {
    promotionInput.locationOfferings.forEach((lo) => {
      const location = lo.location;
      lo.promotables.forEach((promotable) => {
        const promotionItem: LiteralObject = {
          promotable_id: promotable.id,
          promotable_type: promotable.type,
          location_id: location.id,
        };
        const currentPromotionItem = currentPromotionItems.find(
          (pi) =>
            pi.locationId === location.id &&
            pi.promotableType === promotable.type &&
            pi.promotableId === promotable.id
        );
        if (currentPromotionItem) {
          promotionItem["id"] = currentPromotionItem.id;
        }
        promotionItems.push(promotionItem);
      });
    });
  }

  return {
    name: promotionInput.name,
    promotion_code: promotionInput.code,
    description: promotionInput.description,
    start_date: promotionInput.startDate,
    end_date: promotionInput.endDate,
    limit_usage_per_email: promotionInput.limitUsage,
    order_non_refundable: promotionInput.orderNonRefundable,
    offer: {
      type: promotionInput.discountType,
      value: promotionInput.discountValue,
    },
    rule: {
      min_days_booked: promotionInput.minDaysBooked,
      min_hours_booked: promotionInput.minHoursBooked,
      applicable_start_date: promotionInput.applicableStartDate,
      applicable_end_date: promotionInput.applicableEndDate,
    },
    promotion_items: promotionItems,
  };
};
